import { cn } from "@/lib/utils";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDropdownPlacement } from "./use-dropdown-placement";

export type ComboboxOption = {
  value: string;
  label: string;
  /** Optional secondary text shown to the right of the label. */
  hint?: string;
  /** Render the label struck-through + dimmed (e.g. a recently-closed
   *  work item that's still assignable during its grace window). The
   *  option stays fully selectable. */
  struck?: boolean;
  /** Dim the option and refuse to commit it — the native `<option
   *  disabled>` equivalent (e.g. a status a suggestion can't move to
   *  without an assignee yet). */
  disabled?: boolean;
};

type Props = {
  options: ComboboxOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Form name — renders a hidden input so the combobox plugs into a `<form>`. */
  name?: string;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  clearable?: boolean;
  className?: string;
  size?: "default" | "sm";
  "aria-label"?: string;
  "data-testid"?: string;
  /** false renders a plain listbox with no search box — for the fixed,
   *  short enum lists that used to be a native `<select>`. Native
   *  selects were dropped app-wide because Chrome now resolves a
   *  styled `<select>`'s popup as a press-drag-release menu: a plain
   *  click (down+up with no drag) commits whatever option the cursor
   *  lands on, instead of just opening the list. Default true keeps
   *  every existing entity-picker call site unchanged. */
  searchable?: boolean;
};

/**
 * Single-select, searchable by default. Verbatim port of the
 * Next-side `combobox.tsx` (`"use client"` dropped), later widened
 * with `searchable={false}` to also cover the fixed-enum lists that
 * used to be a native `<select>` (see that prop's doc comment).
 *
 * Mirrors native-select muscle memory: click opens (+ focuses the
 * search box when searchable), type filters, arrows move the
 * highlight, Enter selects, Esc / outside-click closes.
 */
export function Combobox({
  options,
  value: controlledValue,
  defaultValue,
  onChange,
  name,
  placeholder: placeholderProp,
  emptyLabel: emptyLabelProp,
  disabled,
  loading,
  clearable,
  className,
  size = "default",
  "aria-label": ariaLabel,
  "data-testid": testId,
  searchable = true,
}: Props) {
  const { t } = useTranslation();
  const placeholder = placeholderProp ?? t("ui.selectPlaceholder");
  const emptyLabel = emptyLabelProp ?? t("ui.noMatches");
  const [uncontrolled, setUncontrolled] = useState<string>(defaultValue ?? "");
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolled;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  // Search box (~2.5rem, skipped when !searchable) + the list's
  // max-h-64 (16rem) ≈ 19rem, or ≈16.5rem without the search box.
  const placement = useDropdownPlacement(wrapperRef, open, searchable ? 304 : 264);

  // The panel portals to <body> (see the render below) so it always paints
  // above page content regardless of which stacking context it opens from —
  // a Select inside one `fixed` bar of several stacked floating bars (e.g.
  // the time-entries bulk-action bars) would otherwise render its panel
  // behind a *later* sibling bar: z-index only wins within a shared
  // stacking context, and two `fixed` siblings each start their own.
  // Portaling needs its own pixel coordinates since it leaves the trigger's
  // `position: relative` wrapper behind.
  // Inside a modal the panel still portals to <body>, not into the dialog:
  // a dialog clips (overflow-y-auto) and, being positioned with a transform,
  // re-bases `fixed` onto itself, so a panel rendered inside it got a
  // scrollbar, was cut off at the dialog's edge and pushed the dialog
  // sideways. The dialog's focus trap is kept out of the way by the nested
  // FocusScope around the panel (see the render below).
  const [coords, setCoords] = useState<{
    left: number;
    minWidth: number;
    maxWidth: number;
    maxHeight: number;
    top?: number;
    bottom?: number;
  }>();
  useLayoutEffect(() => {
    if (!open) return;
    function measure() {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      // minWidth (not width) lets the panel grow past the trigger to fit
      // whichever option has the longest label, instead of mirroring the
      // trigger's own width — which shrinks to fit the *currently selected*
      // option and would truncate every longer one in the list.
      // The panel may stick out past its dialog, but never past the
      // viewport: it starts at the trigger's left edge, pulled back so
      // that at least its minimum width fits, and grows up to the right edge.
      const margin = 8;
      const minWidth = Math.min(rect.width, window.innerWidth - 2 * margin);
      const left = Math.max(margin, Math.min(rect.left, window.innerWidth - minWidth - margin));
      const base = { left, minWidth, maxWidth: window.innerWidth - left - margin };
      // The panel (search box + list) shrinks to the room left in the chosen
      // direction; the list scrolls inside it.
      setCoords(
        placement === "up"
          ? {
              ...base,
              bottom: window.innerHeight - rect.top + 4,
              maxHeight: Math.max(96, rect.top - 4 - margin),
            }
          : {
              ...base,
              top: rect.bottom + 4,
              maxHeight: Math.max(96, window.innerHeight - rect.bottom - 4 - margin),
            },
      );
    }
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, placement]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const haystack = `${o.label} ${o.hint ?? ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, query]);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

  // Reset the search + highlight and focus the input ONLY when the
  // dropdown opens — not when `options` / `value` change identity. A
  // parent that re-renders while the dropdown is open (e.g. the timer
  // widget's 1s elapsed tick) passes a fresh inline `options` array
  // each render; depending on it here would wipe the user's query on
  // every keystroke, so search appears broken inside such modals.
  // biome-ignore lint/correctness/useExhaustiveDependencies: open-transition only — options/value are read at open time on purpose
  useEffect(() => {
    if (open) {
      setQuery("");
      const idx = options.findIndex((o) => o.value === value);
      setHighlight(idx >= 0 ? idx : 0);
      if (searchable) requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(ev: MouseEvent) {
      const target = ev.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      // The panel is portaled to <body> — it's not a DOM descendant of
      // wrapperRef, so it needs its own check or every click inside it
      // would look like an outside click.
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function commit(next: string) {
    if (!isControlled) setUncontrolled(next);
    onChange?.(next);
    setOpen(false);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt && !opt.disabled) commit(opt.value);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  const triggerHeight = size === "sm" ? "h-7 text-xs" : "h-9 text-sm";

  return (
    // min-w-28 floor: as a flex-row child (many call sites drop it into one
    // with no width class), the trigger otherwise shrinks to fit whichever
    // label is *currently selected* — collapsing to near-nothing for a
    // short value like "—". `className` can still widen or override it.
    <div ref={wrapperRef} className={cn("relative min-w-28", className)}>
      {name && <input type="hidden" name={name} value={value} />}

      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKey}
        data-value={value}
        data-testid={testId}
        // biome-ignore lint/a11y/useSemanticElements: this IS the replacement for a native <select> (see the `searchable` prop doc comment) — a real <select> is exactly what it can't be
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        className={cn(
          "flex w-full items-center justify-between gap-1.5 rounded-md border border-input bg-background px-2 py-1",
          "focus:outline-none focus:ring-2 focus:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          triggerHeight,
        )}
      >
        <span className={cn("truncate text-left", !selected && "text-muted-foreground")}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0 text-muted-foreground">
          {clearable && value && !disabled && (
            // biome-ignore lint/a11y/useSemanticElements: a nested <button> is invalid HTML — the outer trigger is already a <button>
            // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard activation happens on the outer button; the X is mouse-only by design
            <span
              role="button"
              tabIndex={-1}
              aria-label={t("ui.clearSelection")}
              onClick={(ev) => {
                ev.stopPropagation();
                commit("");
              }}
              className="rounded hover:bg-accent hover:text-accent-foreground p-0.5"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronsUpDown className="h-3.5 w-3.5" />
        </span>
      </button>

      {open &&
        coords &&
        createPortal(
          <FocusScope asChild trapped>
            <div
              ref={panelRef}
              // Marks the panel as belonging to whatever opened it. It is
              // portalled to <body>, so a modal's focus trap would otherwise
              // treat it as "outside" and pull the keyboard back — see
              // DialogContent, which lets focus and pointer events through to
              // anything carrying this.
              data-combobox-panel=""
              // Focusable container + key handler: without a search box (searchable=false)
              // FocusScope parks focus here, so arrows / Enter / Esc must work from it too.
              // Key events from the search box bubble up to this same handler.
              tabIndex={-1}
              onKeyDown={onKey}
              style={{
                position: "fixed",
                left: coords.left,
                minWidth: coords.minWidth,
                maxWidth: coords.maxWidth,
                maxHeight: coords.maxHeight,
                top: coords.top,
                bottom: coords.bottom,
              }}
              // z-60 keeps it visually above a Dialog's z-50. pointer-events-auto
              // is load-bearing: Radix's modal Dialog sets `body { pointer-events:
              // none }` while open and only opts its own content element back in —
              // this panel is a <body> sibling, not a descendant, so without the
              // override every click on it silently fell through to whatever the
              // dialog painted underneath instead of registering on the option.
              className="z-[60] pointer-events-auto overflow-hidden flex flex-col rounded-md border border-input bg-popover shadow-lg focus:outline-none"
              // Same "body sibling, not descendant" story for wheel/touch: Radix's
              // scroll lock (react-remove-scroll) preventDefault()s any wheel/touch
              // event at the document level unless it originated inside the
              // dialog's own tracked content — stopping propagation here keeps it
              // from ever reaching that document listener, so the list's own
              // native overflow-y scroll goes through untouched.
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              {searchable && (
                <div className="border-b border-input p-1.5">
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setHighlight(0);
                    }}
                    placeholder={t("ui.search")}
                    className="flex h-7 w-full rounded-sm bg-transparent px-1.5 text-sm focus:outline-none"
                  />
                </div>
              )}
              {/* The trigger points here with `aria-controls` and says it
                opens a listbox — so this has to be one, or a screen
                reader is told about a list it then can't find. */}
              <ul
                id={listId}
                // biome-ignore lint/a11y/useSemanticElements: a native <select> is exactly what this replaces — see the `searchable` prop doc comment
                // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: the trigger's aria-controls points here and promises a listbox
                role="listbox"
                aria-label={ariaLabel}
                // Focus stays on the trigger (or the search box); the list
                // is reachable programmatically, which is what the rule asks
                // for without stealing the keyboard from the combobox.
                tabIndex={-1}
                className="max-h-64 min-h-0 flex-1 overflow-y-auto py-1"
              >
                {loading ? (
                  <li className="flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {t("ui.loading")}
                  </li>
                ) : filtered.length === 0 ? (
                  <li className="px-2 py-2 text-sm text-muted-foreground italic">{emptyLabel}</li>
                ) : (
                  filtered.map((o, i) => {
                    const isSel = o.value === value;
                    const isHi = i === highlight;
                    return (
                      <li
                        key={o.value}
                        data-value={o.value}
                        // biome-ignore lint/a11y/useSemanticElements: <option> is only valid inside a native <select>; this is a custom listbox
                        // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: a custom listbox option carries the "option" role by design
                        role="option"
                        tabIndex={-1}
                        aria-selected={isSel}
                        aria-disabled={o.disabled}
                        onMouseEnter={() => !o.disabled && setHighlight(i)}
                        onMouseDown={(ev) => {
                          // mousedown so the click commits BEFORE the outside-click
                          // detector closes the panel and unmounts us.
                          ev.preventDefault();
                          if (!o.disabled) commit(o.value);
                        }}
                        className={cn(
                          "flex items-center justify-between gap-2 px-2 py-1.5 text-sm",
                          o.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                          isHi && !o.disabled && "bg-accent text-accent-foreground",
                        )}
                      >
                        <span className="flex min-w-0 items-center gap-1.5">
                          <Check
                            className={cn(
                              "h-3.5 w-3.5 shrink-0",
                              isSel ? "opacity-100" : "opacity-0",
                            )}
                          />
                          <span className={cn("truncate", o.struck && "line-through opacity-60")}>
                            {o.label}
                          </span>
                        </span>
                        {o.hint && (
                          <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                            {o.hint}
                          </span>
                        )}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </FocusScope>,
          document.body,
        )}
    </div>
  );
}
